import type {
	CACardActions,
	CACardType,
	CAHeadingLevel,
} from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

import CAHeading from '../headers/CAHeading'

/**
 * A card with a title and content.
 *
 * @component CACard
 *
 * @prop {CACardActions[]} [actions=[]] - The actions for the card.
 * @prop {string} [dataTest=''] - The data-test attribute for the card
 * @prop {boolean} [defaultTag=false] - If this card is the default, show tag.
 * @prop {string} [description=''] - For accessibility purposes, the description of the card.
 * @prop {boolean} [disableTitleMutation=true] - Whether the title capitalization should be overwritten.
 * @prop {boolean} [editTitle=false] - Whether the title should be editable. If this is selected, an input will be available that will emit the new title.
 * @prop {string} [headerActionText=''] - The text for the header action.
 * @prop {string} [headerIcon=''] - The icon for the header.
 * @prop {string} [headerIconAfterTitle=''] - The icon for the end of the title. Find the icons in the Icons page of the documentation and use their name.
 * @prop {string} [headerIconBeforeTitle=''] - The icon for the start of the title. Find the icons in the Icons page of the documentation and use their name.
 * @prop {CAHeadingLevel} [heading=6] - The heading level of the heading. Line is a special one for a line under the heading. Only use if specified.
 * @prop {boolean} [loading=false] - Whether the card title is in a loading state.
 * @prop {number} [minCharacterCount=3] - The minimum character count for the title.
 * @prop {string | null} [modelValue=null] - The model value for the card.
 * @prop {string | null} [title=null] - The title of the card.
 * @prop {CACardType} [type='default'] - The type of the card.
 *
 * @slot action - The action slot for the card.
 * @slot default - The default slot for the card content.
 * @slot footer - The footer slot for the card.
 *
 * @emit click - The click event in case you prefer to use this as an event trigger for the action.
 * @emit clickTitle - Emits when the title is clicked.
 * @emit headingClick - The headingClick event in case you prefer to use this as an event trigger via props only.
 * @emit update:modelValue - The update event in case you prefer to use this as an event trigger.
 */
export default defineComponent({
	name: 'CACard',

	props: {
		/**
		 * The actions for the card.
		 */
		actions: { default: () => [], type: Array as PropType<CACardActions[]> },
		/**
		 * The data-test attribute for the card.
		 */
		dataTest: { default: '', type: String },
		/**
		 * If this card is the default, show tag.
		 *
		 * @default false
		 */
		defaultTag: { default: false, type: Boolean },
		/**
		 * For accessibility purposes, the description of the card.
		 */
		description: { default: '', type: String },
		/**
		 * Whether the title capitalization should be overwritten.
		 */
		disableTitleMutation: { default: true, type: Boolean },
		/**
		 * Whether the title should be editable. If this is selected, an input will be available that will emit the new
		 * title.
		 *
		 * @default false
		 */
		editTitle: { default: false, type: Boolean },
		/**
		 * The text for the header action.
		 */
		headerActionText: { default: '', type: String },
		/**
		 * The icon for the header.
		 */
		headerIcon: { default: '', type: String },
		/**
		 * The icon for the end of the title. Find the icons in the Icons page of the documentation and use their name.
		 */
		headerIconAfterTitle: { default: '', type: String },
		/**
		 * The icon for the start of the title. Find the icons in the Icons page of the documentation and use their name.
		 */
		headerIconBeforeTitle: { default: '', type: String },
		/**
		 * The heading level of the heading. Line is a special one for a line under the heading. Only use if specified.
		 *
		 * @default 6
		 */
		heading: {
			default: 6, // eslint-disable-line no-magic-numbers
			type: [
				String,
				Number,
			] as PropType<CAHeadingLevel>,
		},
		/**
		 * Whether the card title is in a loading state.
		 *
		 * @default false
		 */
		loading: { default: false, type: Boolean },
		/**
		 * The minimum character count for the title.
		 *
		 * @default 3
		 */
		minCharacterCount: { default: 3, type: Number },
		modelValue: { default: null, type: String as PropType<string | null> },
		/**
		 * The title of the card.
		 */
		title: { default: null, type: String as PropType<string | null> },
		/**
		 * The type of the card.
		 *
		 * @default 'title'
		 * @enum {'default' | 'table' | 'title' | 'list' | 'glass'}
		 */
		type: { default: 'title', type: String as PropType<CACardType> },
	},

	emits: {
		'update:modelValue': (_value: string) => true,
		click: (_event: Event) => true,
		clickTitle: () => true,
		headingClick: () => true,
	},

	data(): Data {
		return { boundTitle: this.modelValue ?? this.title ?? '' }
	},

	watch: {
		modelValue(newValue) {
			this.boundTitle = newValue
		},

		title(newTitle) {
			this.boundTitle = newTitle
		},
	},

	methods: {
		handleCardClick(event: Event) {
			this.$emit('click', event)
		},

		handleInput(newTitle: string) {
			this.boundTitle = newTitle
			this.$emit('update:modelValue', newTitle)
		},

		update(newTitle: string) {
			this.$emit('update:modelValue', newTitle)
		},
	},

	render() {
		return (
			<div
				aria-describedby={this.description}
				aria-labelledby={`${this.dataTest}-heading`}
				class={[
					`ca-card ca-card__${this.type}`,
					{
						'ca-card--has-header-action': !!this.headerActionText,
						'ca-card--with-header-icon': this.headerIcon !== '' ||
							this.headerIconAfterTitle !== '' ||
							this.headerIconBeforeTitle !== '',
						'ca-card--with-footer': !!this.$slots.footer?.(),
					},
				]}
				data-test={this.dataTest}
				data-test-component="ca-card"
				role="region"
				onClick={(event: Event) => this.handleCardClick(event)}
			>
				{this.boundTitle &&
					<CAHeading
						class="ca-card__header ca-heading--card-title"
						data-test={`${this.dataTest}-heading`}
						disableTitleMutation={this.disableTitleMutation}
						editTitle={this.editTitle}
						headerActionText={this.headerActionText}
						heading={this.heading}
						icon={this.headerIcon}
						iconAfterTitle={this.headerIconAfterTitle}
						iconBeforeTitle={this.headerIconBeforeTitle}
						minCharacterCount={this.minCharacterCount}
						v-model={this.boundTitle}
						onClick={() => this.$emit('headingClick')}
						onClickTitle={() => this.$emit('clickTitle')}
						onUpdate:modelValue={(value: string) => this.update(value)}
					>
						{{
							action: () => this.$slots.action?.(),
							default: () => this.boundTitle,
						}}
					</CAHeading>
				}

				<div class="ca-card__content">
					{this.$slots.default?.()}
				</div>

				{this.$slots.footer?.() &&
					<div class="ca-card__footer" data-test="ca-card-footer">
						{this.$slots.footer?.()}
					</div>
				}
			</div>
		)
	},
})

interface Data {
	boundTitle: string
}
